import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { CronField } from "../components/CronField";
import { TimezonePicker } from "../components/TimezonePicker";
import {
  Alert,
  Button,
  Card,
  Combobox,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Switch,
  Textarea,
  buttonStyles,
} from "../components/ui";
import { validateCron } from "../lib/cron";
import { DEFAULT_TIMEZONE } from "../lib/timezones";
import { useApps, useCreateSchedule } from "../lib/queries";

export function NewSchedulePage() {
  const { t } = useTranslation("create");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Deep-linked from an app's own Schedules tab, so arriving from there
  // pre-selects the app somebody was already looking at.
  const presetAppId = params.get("appId");

  const apps = useApps(orgId, 100);
  const create = useCreateSchedule(orgId ?? "");

  const [appId, setAppId] = useState(presetAppId ?? "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cronExpression, setCronExpression] = useState("0 3 * * *");
  const [command, setCommand] = useState("");
  const [shellType, setShellType] = useState<"bash" | "sh">("bash");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [enabled, setEnabled] = useState(true);

  /*
   * A draft app has no container to exec into, so it cannot carry a schedule.
   * Filtered rather than shown-and-refused: an option that always errors is a
   * worse answer than an option that is not there, and the note below says why
   * the list may be short.
   */
  const selectable = (apps.data?.data ?? []).filter((app) => app.status.value !== "draft");
  const chosen = selectable.find((app) => app.id === appId);

  /*
   * Running apps first, then stopped ones. Both can carry a schedule, but a
   * stopped app is the surprising choice — grouping says so without refusing
   * it, and the warning below says why once one is picked.
   */
  const appGroups = useMemo(() => {
    const running = selectable.filter((app) => app.status.value !== "stopped");
    const stopped = selectable.filter((app) => app.status.value === "stopped");
    return [
      { label: t("schedule.appsRunning"), options: running.map((app) => ({ value: app.id, label: app.name })) },
      {
        label: t("schedule.appsStopped"),
        options: stopped.map((app) => ({ value: app.id, label: app.name, hint: t("schedule.stoppedHint") })),
      },
    ].filter((group) => group.options.length > 0);
  }, [selectable]);

  const cronCheck = validateCron(cronExpression);
  const ready = Boolean(appId) && name.trim().length > 0 && command.trim().length > 0 && cronCheck.ok;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready || !orgId) return;

    const schedule = await create.mutateAsync({
      appId,
      name: name.trim(),
      description: description.trim() || undefined,
      cronExpression: cronExpression.trim(),
      command: command.trim(),
      shellType,
      timezone,
      enabled,
    });

    navigate(`/schedules/${schedule.id}`);
  }

  if (apps.isPending) return <LoadingBlock />;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("schedule.title")}
        description={t("schedule.description")}
      />

      {create.error && <Alert>{create.error.message}</Alert>}

      {selectable.length === 0 ? (
        <Card>
          <div className="space-y-3 p-6 text-[13px]">
            <p className="text-fg">{t("schedule.needsAppLead")}</p>
            <p className="text-fg-muted">{t("schedule.needsApp")}</p>
            <Link to="/apps/new" className={buttonStyles("primary")}>
              {t("schedule.createApp")}
            </Link>
          </div>
        </Card>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <Card>
            <div className="space-y-5 p-5">
              {/* First field on the page: which app this runs in is the decision
                  everything else hangs off. */}
              <Field
                label={t("schedule.app")}
                htmlFor="schedule-app"
                hint={t("schedule.appHint")}
              >
                {/*
                  A combobox rather than a select, for the same reason the
                  timezone field is one: an organization with thirty apps is a
                  scroll to find one by eye, and a native select's type-ahead
                  only matches from the first character.
                */}
                <Combobox
                  id="schedule-app"
                  value={appId}
                  onChange={setAppId}
                  groups={appGroups}
                  placeholder={t("schedule.searchApps")}
                  emptyLabel={t("schedule.noAppMatch")}
                />
              </Field>

              {chosen?.status.value === "stopped" && (
                <Alert tone="warning">
                  {t("schedule.appStopped")}
                </Alert>
              )}

              <Field label={t("schedule.name")} hint={t("schedule.nameHint")}>
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="nightly-prune"
                  maxLength={60}
                  required
                />
              </Field>

              <Field label={t("schedule.description")} hint={t("schedule.descriptionHint")}>
                <Input
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={t("schedule.descriptionPlaceholder")}
                  maxLength={500}
                />
              </Field>
            </div>
          </Card>

          <Card>
            <div className="space-y-5 p-5">
              <CronField
                value={cronExpression}
                onChange={setCronExpression}
                timezone={timezone}
              />

              <TimezonePicker value={timezone} onChange={setTimezone} />
            </div>
          </Card>

          <Card>
            <div className="space-y-5 p-5">
              <Field
                label={t("schedule.command")}
                hint={t("schedule.commandHint")}
              >
                <Textarea
                  value={command}
                  onChange={(event) => setCommand(event.target.value)}
                  placeholder={t("schedule.commandPlaceholder")}
                  rows={3}
                  maxLength={2000}
                  spellCheck={false}
                  className="font-mono"
                  required
                />
              </Field>

              <Field
                label={t("schedule.shell")}
                hint={t("schedule.shellHint")}
              >
                <Select
                  value={shellType}
                  onChange={(event) => setShellType(event.target.value as "bash" | "sh")}
                >
                  <option value="bash">bash</option>
                  <option value="sh">sh</option>
                </Select>
              </Field>

              {/* Switch carries no visible text of its own, so the label and
                  the caveat sit beside it — matching BackupPanel. */}
              <div className="flex items-center gap-3">
                <Switch checked={enabled} onChange={setEnabled} label={t("schedule.enabled")} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-fg">
                    {enabled ? t("schedule.enabledNote") : t("schedule.disabled")}
                  </p>
                  <p className="mt-0.5 text-[13px] text-fg-subtle">
                    {t("schedule.pausedNote")}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/*
            The price, stated before they commit rather than discovered on the
            next invoice. Priced from day one deliberately: §6.5 of the Terms
            would otherwise require 30 days' notice to start charging later.
          */}
          <Card>
            <div className="flex items-center justify-between gap-4 p-5">
              <div className="text-[13px]">
                <p className="text-fg">{t("schedule.price")}</p>
                <p className="text-fg-muted">
                  {t("schedule.billing")}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Link to="/schedules" className={buttonStyles("secondary")}>
                  {t("shared.cancel")}
                                    </Link>
                <Button type="submit" disabled={!ready} loading={create.isPending}>
                  {t("schedule.create")}
                                    </Button>
              </div>
            </div>
          </Card>
        </form>
      )}
    </div>
  );
}
